<script setup lang="ts">
import { onUnmounted } from 'vue';

import { RemoveHotelSelectionFiltersOrigin } from '@apps/main/new-desktop/core/models';
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import {
  HotelFilter,
  HotelFiltersTags,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';

import {
  useHotelSelectionMapStore,
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';

import HotelSelectionContentDesktopLayout from './HotelSelectionContentDesktopLayout.vue';
import HotelSelectionDestination from './HotelSelectionDestination.vue';
import HotelSelectionFiltersPanel from './HotelSelectionFiltersPanel.vue';
import HotelSelectionList from './HotelSelectionList.vue';
import HotelSelectionListHeader from './HotelSelectionListHeader.vue';
import HotelSelectionListLoader from './HotelSelectionListLoader.vue';
import HotelSelectionMap from './HotelSelectionMap.vue';

const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionMapStore = useHotelSelectionMapStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const browserStore = useBrowserStore();
const filtersStore = useFiltersStore();

interface HotelSelectionContentDesktopEmits {
  (eventName: 'initialized-hotel-selection-list'): void;
}

const emit = defineEmits<HotelSelectionContentDesktopEmits>();

function onHotelSelectionFilterUpdateFromTags(
  filterUpdate: HotelFilterUpdate,
  id?: string
): void {
  if (filterUpdate.type === HotelFilter.NeighborhoodPolygons && id) {
    filterUpdate.value = id;
  }
  filtersStore.updateHotelFilter(
    filterUpdate,
    RemoveHotelSelectionFiltersOrigin.HotelList,
    true
  );
}

onUnmounted(() => {
  hotelSelectionMapStore.onToggleFullScreen(false);
});
</script>

<template>
  <HotelSelectionContentDesktopLayout
    class="hotel-selection-content"
    :full-screen-map="hotelSelectionMapStore.isFullScreen"
  >
    <template v-if="browserStore.isLargerThan1535px" #leftColumn>
      <HotelSelectionFiltersPanel
        @close="hotelSelectionStore.openFilters()"
        @update-filter="
          filtersStore.updateHotelFilter(
            $event,
            RemoveHotelSelectionFiltersOrigin.FilterList
          )
        "
      />
    </template>

    <template #middleColumnTop>
      <div v-if="browserStore.isLargerThan1535px" class="list-top-section">
        <HotelSelectionDestination
          v-if="hotelSelectionStore.hotelSelectionHeaderData"
          :data="hotelSelectionStore.hotelSelectionHeaderData"
          class="destination"
          @open-filters="hotelSelectionStore.openFilters()"
          @change-number-of-nights="hotelSelectionStore.changeNumberOfNights"
          @change-destination="
            hotelSelectionModalsStore.openDestinationSelectorModal(
              $event,
              hotelSelectionStore.editingIngredientId
            )
          "
        />
        <HotelFiltersTags
          v-if="filtersStore.hasActiveHotelFilters()"
          :ingredient="hotelSelectionStore.editingStayIngredient"
          class="tags"
          @update-filter="
            onHotelSelectionFilterUpdateFromTags($event.type, $event.id)
          "
        />
        <HotelSelectionListHeader
          class="list-header"
          :hotels-total="hotelSelectionStore.hotelSelectionCards.length"
          :suggestion-type="hotelSelectionStore.listSuggestionType"
          @suggestion-type-selected="hotelSelectionStore.setListSuggestionType"
          @show-why-we-suggest="hotelSelectionModalsStore.openWhyWeSuggestModal"
        />
      </div>
    </template>
    <template #middleColumnBottom>
      <HotelSelectionListLoader
        v-if="hotelSelectionStore.shouldDisplayLoader"
        class="list-cards"
      />
      <HotelSelectionList
        v-else
        class="list-cards"
        :data="hotelSelectionStore.hotelSelectionCards"
        @hightlight-on-map="hotelSelectionMapStore.hightlightOnMap($event)"
        @initialized-hotel-selection-list="
          emit('initialized-hotel-selection-list')
        "
      />
    </template>

    <template #rightColumn>
      <HotelSelectionMap
        @toggle-full-screen="
          hotelSelectionMapStore.onToggleFullScreen(
            !hotelSelectionMapStore.isFullScreen
          )
        "
      />
    </template>
  </HotelSelectionContentDesktopLayout>
</template>

<style scoped lang="scss">
.hotel-selection-content {
  .destination {
    margin-bottom: var(--spacing-8);
  }

  .pills {
    padding-top: var(--spacing-8);
    margin-bottom: var(--spacing-8);
  }
  .list-top-section {
    padding: var(--spacing-24) var(--spacing-24) 0 var(--spacing-24);
    .list-header {
      margin-bottom: var(--spacing-8);
    }
  }

  .list-cards {
    padding: 0 var(--spacing-24) 0 var(--spacing-24);
  }
}
</style>
